/**
 * Lazy Loading Timeline Component
 * 
 * Uses intersection observer and virtualization for performance
 * Only renders items that are visible in the viewport
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Mail, 
  Calendar, 
  Mic, 
  CheckSquare,
  Clock,
  User
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { cn } from '@/lib/utils';

interface TimelineItem {
  id: string;
  type: 'email' | 'meeting' | 'journal' | 'task';
  date: string;
  title: string;
  description?: string;
  contact?: string;
  contactEmail?: string;
  participants?: string[];
  metadata?: {
    direction?: 'sent' | 'received';
    isUrgent?: boolean;
    isCompleted?: boolean;
    status?: string;
    priority?: string;
  };
}

interface LazyTimelineProps {
  items: TimelineItem[];
  className?: string;
}

const typeIcons = {
  email: Mail,
  meeting: Calendar,
  journal: Mic,
  task: CheckSquare,
};

const typeColors = {
  email: 'text-blue-500 bg-blue-500/10',
  meeting: 'text-green-500 bg-green-500/10',
  journal: 'text-purple-500 bg-purple-500/10',
  task: 'text-orange-500 bg-orange-500/10',
};

const typeLabels = {
  email: 'Email',
  meeting: 'Meeting',
  journal: 'Voice Journal',
  task: 'Task',
};

// Virtual scrolling configuration
const ITEM_HEIGHT = 180; // Approximate height of each item
const BUFFER_SIZE = 5; // Number of items to render outside viewport

export function LazyTimeline({ items, className }: LazyTimelineProps) {
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 20 });
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(0);

  // Calculate which items should be visible based on scroll position
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollTop = container.scrollTop;
    const viewportHeight = container.clientHeight;

    // Calculate visible range with buffer
    const startIndex = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - BUFFER_SIZE);
    const endIndex = Math.min(
      items.length,
      Math.ceil((scrollTop + viewportHeight) / ITEM_HEIGHT) + BUFFER_SIZE
    );

    setVisibleRange({ start: startIndex, end: endIndex });
  }, [items.length]);

  // Set up scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Initial calculation
    handleScroll();
    setContainerHeight(container.clientHeight);

    // Throttled scroll handler
    let ticking = false;
    const scrollHandler = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    container.addEventListener('scroll', scrollHandler, { passive: true });
    
    // Handle resize
    const resizeObserver = new ResizeObserver(() => {
      setContainerHeight(container.clientHeight);
      handleScroll();
    });
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', scrollHandler);
      resizeObserver.disconnect();
    };
  }, [handleScroll]);

  // Calculate total height and visible items
  const totalHeight = items.length * ITEM_HEIGHT;
  const visibleItems = items.slice(visibleRange.start, visibleRange.end);
  const offsetY = visibleRange.start * ITEM_HEIGHT;

  if (items.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p className="text-lg mb-2">No items found</p>
        <p className="text-sm">Your timeline will appear here</p>
      </div>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      className={cn('relative overflow-y-auto', className)}
      style={{ height: containerHeight || '600px' }}
    >
      {/* Spacer for total content height */}
      <div style={{ height: `${totalHeight}px`, position: 'relative' }}>
        {/* Visible items container */}
        <div
          style={{
            position: 'absolute',
            top: `${offsetY}px`,
            left: 0,
            right: 0,
          }}
        >
          <div className="space-y-4 pr-4">
            {visibleItems.map((item, index) => {
              const Icon = typeIcons[item.type];
              const colorClass = typeColors[item.type];
              const label = typeLabels[item.type];
              const actualIndex = visibleRange.start + index;

              return (
                <div key={item.id}>
                  <div className="flex items-start gap-4 p-4 rounded-lg hover:bg-secondary/50 transition-colors">
                    {/* Icon */}
                    <div className={cn('p-3 rounded-lg flex-shrink-0', colorClass)}>
                      <Icon className="w-5 h-5" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {/* Header */}
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline">{label}</Badge>
                          
                          {item.metadata?.priority && (
                            <Badge 
                              variant={
                                item.metadata.priority === 'high' ? 'destructive' : 
                                item.metadata.priority === 'medium' ? 'default' : 
                                'secondary'
                              }
                            >
                              {item.metadata.priority}
                            </Badge>
                          )}
                          
                          {item.metadata?.isCompleted && (
                            <Badge variant="default" className="bg-green-500/10 text-green-600">
                              Completed
                            </Badge>
                          )}
                          
                          {item.metadata?.isUrgent && (
                            <Badge variant="destructive">Urgent</Badge>
                          )}
                        </div>

                        <div className="flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
                          <Clock className="w-3 h-3" />
                          {formatDistanceToNow(new Date(item.date), { addSuffix: true })}
                        </div>
                      </div>

                      {/* Title */}
                      <h4 className="font-semibold mb-1 text-sm">{item.title}</h4>

                      {/* Description */}
                      {item.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                          {item.description}
                        </p>
                      )}

                      {/* Contact */}
                      {item.contact && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <User className="w-3 h-3" />
                          <span>{item.contact}</span>
                          {item.contactEmail && (
                            <span className="text-muted-foreground/60">({item.contactEmail})</span>
                          )}
                        </div>
                      )}

                      {/* Participants */}
                      {item.participants && item.participants.length > 0 && (
                        <div className="flex items-center gap-1 mt-2 flex-wrap">
                          <span className="text-xs text-muted-foreground">With:</span>
                          {item.participants.slice(0, 3).map((p, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              {p}
                            </Badge>
                          ))}
                          {item.participants.length > 3 && (
                            <span className="text-xs text-muted-foreground">
                              +{item.participants.length - 3} more
                            </span>
                          )}
                        </div>
                      )}

                      {/* Full date */}
                      <div className="text-xs text-muted-foreground/60 mt-2">
                        {format(new Date(item.date), 'PPpp')}
                      </div>
                    </div>
                  </div>

                  {actualIndex < items.length - 1 && (
                    <Separator className="my-2" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Loading indicator at bottom */}
      {visibleRange.end < items.length && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs text-muted-foreground">
          Scroll to load more...
        </div>
      )}
    </div>
  );
}
